<script setup lang="ts">
  import { StockItemsService } from '@/api/services/stock-items.service';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { IStockItem } from '@/interfaces/IStockItem';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
  import ModalsStockItem from './ModalsStockItem.vue';
  import { IDataOptions } from '@/interfaces/IDataOptions';

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();

  const paginationData = ref<IDataOptions>({
    page: 1,
    itemsPerPage: 5,
    mustSort: true,
    sortBy: ['name'],
    sortDesc: [false],
    search: null,
  });

  const totalItems = ref(0);

  const stockItems = ref<IStockItem[] | null>(null);

  const loading = ref(false);

  async function loadStockItems() {
    if (loading.value) return;

    try {
      loading.value = true;
      const response = await StockItemsService.getAll({
        restaurantId: restaurantStore.restaurant.id,
        branchId: restaurantStore.selectedBranch.id,
        options: paginationData.value,
      });
      stockItems.value = response.items;
      totalItems.value = response.total;
    } catch (error) {
      ui.serverError(error, 'Error al cargar los componentes de stock');
    } finally {
      loading.value = false;
    }
  }

  watch(
    paginationData,
    () => {
      loadStockItems();
    },
    { immediate: true, deep: true }
  );

  const filteredStockItems = computed(() => {
    /*if (!paginationData.value.search) return stockItems.value;

    return stockItems.value?.filter?.((item) =>
      item.name
        .toLowerCase()
        .includes(paginationData.value.search.toLowerCase() || '')
    );*/
    return stockItems.value;
  });

  const { proxy } = getCurrentInstance();

  function handleChooseItem(item: IStockItem) {
    proxy.$parent.$emit('chooseItem', item);
  }

  function openNewStockItemModal() {
    modals.show(
      ModalsStockItem,
      {},
      {
        name: 'stock-item',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        saved: (item: IStockItem) => {
          handleChooseItem(item);
        },
      }
    );
  }
</script>

<template>
  <v-card class="modal-stock-items">
    <v-card-title>
      <span>Componentes de stock</span>
    </v-card-title>
    <v-card-text class="card-text">
      <div class="modal-body">
        <div class="modal-body-header">
          <div class="search-container">
            <v-text-field
              v-model="paginationData.search"
              placeholder="Buscar"
              outlined
              rounded
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
            />
          </div>
          <v-btn
            color="primary"
            rounded
            depressed
            @click="openNewStockItemModal"
          >
            <v-icon left>mdi-plus</v-icon>
            <span>Nuevo</span>
          </v-btn>
        </div>
        <div class="modal-body-content">
          <v-list>
            <v-list-item v-for="item in filteredStockItems" :key="item.id">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>{{
                  item.description
                }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  color="success"
                  rounded
                  depressed
                  small
                  @click="handleChooseItem(item)"
                >
                  <v-icon left>mdi-check</v-icon>
                  <span>Elegir</span>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <div class="tw-flex tw-justify-center tw-items-center tw-mt-4">
            <v-pagination
              v-model="paginationData.page"
              :length="Math.ceil(totalItems / paginationData.itemsPerPage)"
              :total-visible="5"
            />
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .modal-stock-items {
    @apply tw-bg-transparent tw-border-none tw-h-full;

    .card-text {
      @apply tw-flex-1 tw-min-h-1 tw-overflow-y-auto;

      .modal-body {
        @apply tw-space-y-2;

        .modal-body-header {
          @apply tw-flex tw-justify-between tw-items-center;
        }

        .modal-body-content {
          @apply tw-flex-1 tw-min-h-1 tw-overflow-y-auto;
        }
      }
    }
  }
</style>
